import qs from 'qs';
import { catchFetchApiError, fetchApi, getFetchApiJSONResponse, getUserId } from 'shared/functions';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { serviceUrlPrefix } from 'utils';

const entitiesUrlByRatingType = {
  [ratingTypes.CREDIT_RATING]: '/storefront/issuers/',
  [ratingTypes.CREDIT_REVIEW]: '/sig/api/companies/',
  [ratingTypes.ESG_IMPACT]: '/esg/api/companies/',
  [ratingTypes.SENTIMENT]: '/sentifi/companies',
  [ratingTypes.CLIMATE_STRESS_TEST]: '/esg/climate-stress-test/entity/search/list',
};

export const fetchEntityDocuments = ({ entityId, page = 1, limit = 200 }) => {
  const userId = getUserId();

  const params = {
    page,
    limit,
  };

  const resourceUrl = `/storefront/users/${userId}/issuers/${entityId}/documents?${qs.stringify(
    params,
  )}`;

  return fetchApi(resourceUrl).then((response) => response.json());
};

export const fetchEntityInstruments = async ({ entityId, page, limit, sort, filter }) => {
  const params = {
    page,
    limit,
    ...(sort && { sort: sort.sortColumn }),
    ...(sort && { dir: sort.direction }),
  };

  const filterString = filter ? `&filter=${encodeURI(JSON.stringify(filter))}` : '';

  return fetchApi(
    `/storefront/issuers/${entityId}/issues?${qs.stringify(params)}${filterString}`,
  ).then((response) => response.json());
};

export const fetchEntityProducts = ({ entityId }) => {
  const userId = getUserId();

  return fetchApi(`/storefront/users/${userId}/issuers/${entityId}/products`)
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const fetchEntities = async (
  { query, limit, page, sort, filter },
  ratingType = ratingTypes.CREDIT_RATING,
) => {
  const params = {
    query,
    page,
    limit,
    ...(sort && { sort: sort.sortColumn }),
    ...(sort && { dir: sort.direction }),
  };

  const filterQuery = filter;
  // industrySectors mapping reason explained in SCI-3628
  if (ratingType === ratingTypes.CREDIT_RATING && filter.industrySectors) {
    filterQuery.industrySectors = filter.industrySectors.map((industrySector) => {
      const { industry, sector } = industrySector;
      return industry === sector ? { sector } : industrySector;
    });
  }

  const filterString = filter ? `&filter=${encodeURI(JSON.stringify(filterQuery))}` : '';
  const entitiesUrl = entitiesUrlByRatingType[ratingType];

  try {
    return await fetchApi(`${entitiesUrl}?${qs.stringify(params)}${filterString}`)
      .then((response) => response.json());
  } catch (response) {
    const data = await response.json();
    const [status, message] = typeof data === 'string' ? [response.status, data] : data;
    return { error: { status, message } };
  }
};

export const fetchEntityBasic = async ({ id, ratingType = ratingTypes.CREDIT_RATING, params = null }) => {
  const urlPrefix = serviceUrlPrefix[ratingType];

  return fetchApi(`${urlPrefix}/${id}/details`, { query: params }, [], true).then((response) =>
    response.json(),
  );
};

/*
  Returns entity data either from credit rating database,
  or from esg, depending on whitch one is available.
*/
export const fetchEntity = async ({ id, params }) => {
  const userId = getUserId();

  const fetchData = (url) =>
    fetchApi(url, { query: params }, [], false)
      .then((response) => response.json())
      .catch((response) => response);

  const [creditRatingData, esgData] = await Promise.all([
    fetchData(`/storefront/issuers/${id}`),
    fetchData(`/esg/api/${id}/details`),
  ]);

  /* Return entity from credit rating database if available */
  if (creditRatingData.data) {
    return {
      entityApiData: creditRatingData,
      documentsApiData: await fetchEntityDocuments({ entityId: id, page: 1, limit: 200 }),
      productsApiData: await fetchEntityProducts({ entityId: id, userId }),
    };
  }

  /* Fallback to entity from ESG database if not found in credit rating */
  if (esgData.data) {
    return {
      entityApiBasic: esgData,
    };
  }

  return Promise.reject(creditRatingData);
};

export const fetchEntityDetails = async (id, params) => {
  const userId = getUserId();
  const entityCall = fetchApi(`/storefront/issuers/${id}`, { query: params }, [], true).then(
    (response) => response.json(),
  );
  const productsCall = fetchEntityProducts({ entityId: id, userId });

  return {
    entityApiData: await entityCall,
    productsApiData: await productsCall,
  };
};
